import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Grid,
  Card,
  Badge,
  rem,
  SimpleGrid,
  RingProgress,
  ThemeIcon,
  Table,
  ScrollArea
} from '@mantine/core';
import { YearPickerInput } from '@mantine/dates';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconWallet,
  IconCoin,
  IconShoppingCart,
  IconChartBar,
  IconGift,
  IconSum,
} from '@tabler/icons-react';
import {
  LineChart,
  Line,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { collection, query, where, getDocs } from 'firebase/firestore';
import firestore from '../../utils/firestore';
import format_with_substring from '../../utils/number';

// 設置 dayjs 語言
dayjs.locale('zh-tw');

const ElderlyBoardIndex = () => {
  const [showDate, setShowDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [yearlyStats, setYearlyStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    balance: 0,
    monthlyData: Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      monthName: `${i + 1}月`,
      generalIncome: 0,
      specialIncome: 0,
      income: 0,
      expenses: 0,
      balance: 0
    }))
  });

  const year = showDate.year();

  // 獲取年度資料
  useEffect(() => {
    const fetchData = async () => {
      const q = query(
        collection(firestore, 'accounting'),
        where('year', '==', year)
      );
      const accounting = await getDocs(q);
      
      if (accounting.docs.length > 0) {
        const records = [];
        accounting.forEach(doc => {
          records.push({ id: doc.id, ...doc.data() });
        });
        setData(records);
        calculateYearlyStats(records);
      } else {
        setData([]);
        setYearlyStats({
          totalIncome: 0,
          totalExpenses: 0,
          balance: 0,
          monthlyData: Array.from({ length: 12 }, (_, i) => ({
            month: i + 1,
            monthName: `${i + 1}月`,
            generalIncome: 0,
            specialIncome: 0,
            income: 0,
            expenses: 0,
            balance: 0
          }))
        });
      }
    };
    fetchData();
  }, [showDate, year]);

  // 計算年度統計
  const calculateYearlyStats = (records) => {
    let totalIncome = 0;
    let totalExpenses = 0;
    const monthlyData = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      monthName: `${i + 1}月`,
      generalIncome: 0,
      specialIncome: 0,
      income: 0,
      expenses: 0,
      balance: 0
    }));

    records.forEach(record => {
      const generalIncome = record.income || 0;
      const specialIncome = record.specialIncome || 0;
      const income = generalIncome + specialIncome;
      const expenses = record.expenses || 0;
      
      totalIncome += income;
      totalExpenses += expenses;
      
      if (record.month >= 0 && record.month < 12) {
        monthlyData[record.month].generalIncome += generalIncome;
        monthlyData[record.month].specialIncome += specialIncome;
        monthlyData[record.month].income += income;
        monthlyData[record.month].expenses += expenses;
        monthlyData[record.month].balance += (income - expenses);
      }
    });

    const newStats = {
      totalIncome,
      totalExpenses,
      balance: totalIncome - totalExpenses,
      monthlyData
    };
    
    setYearlyStats(newStats);
  };

  // 獲取儲蓄率
  const getSavingsRate = () => {
    if (yearlyStats.totalIncome === 0) return 0;
    return Math.round((yearlyStats.balance / yearlyStats.totalIncome) * 100);
  };

  // 獲取最佳月份
  const getBestMonth = () => {
    if (yearlyStats.monthlyData.length === 0) {
      return { monthName: '-', balance: 0, income: 0, expenses: 0 };
    }
    const best = yearlyStats.monthlyData.reduce((prev, current) => 
      (current.balance > prev.balance) ? current : prev
    );
    return best;
  };

  // 獲取支出最高月份
  const getHighestExpenseMonth = () => {
    if (yearlyStats.monthlyData.length === 0) {
      return { monthName: '-', expenses: 0, income: 0, balance: 0 };
    }
    const highest = yearlyStats.monthlyData.reduce((prev, current) => 
      (current.expenses > prev.expenses) ? current : prev
    );
    return highest;
  };

  const savingsRate = getSavingsRate();
  const bestMonth = getBestMonth();
  const highestExpenseMonth = getHighestExpenseMonth();

  // 產生支出表格資料
  const generateExpenseTableData = () => {
    const vertical = [
      '收入',
      '',
      '小神堂',
      '源吉',
      '祥雲',
      '揚明',
      '伯明',
      '吉興',
      '祐恆',
      '香',
      '',
      '包-花袋',
      '雜支',
      '營業稅',
      '打火機',
      ''
    ];
    const showData = [];

    // 處理資料
    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      const month = element.month + 1;
      
      // 計算收入
      if (!showData[0]) {
        showData[0] = {};
      }
      if (!showData[0][month]) {
        showData[0][month] = 0;
      }
      showData[0][month] += element.income || 0;
      showData[0][month] += element.specialIncome || 0;

      // 計算支出
      let index = vertical.indexOf(element.vendor);
      if (index === -1) {
        index = vertical.length;
        vertical.push(element.vendor);
      }
      if (!showData[index]) {
        showData[index] = {};
      }
      if (showData[index][month]) {
        showData[index][month] += element.expenses || 0;
      } else {
        showData[index][month] = element.expenses || 0;
      }
    }

    // 計算總計
    for (let i = 0; i < showData.length; i++) {
      if (!showData[i]) {
        showData[i] = {};
      }
      showData[i].title = vertical[i];
      showData[i].key = vertical[i] || i;
      showData[i].total = 0;
      for (let j = 1; j <= 12; j++) {
        if (showData[i][j]) {
          showData[i].total += showData[i][j] || 0;
        }
      }
    }

    return { showData, vertical };
  };

  // 產生特殊收入表格資料
  const generateSpecialIncomeTableData = () => {
    const vertical = [];
    const showData = [];
    
    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      const month = element.month + 1;
      
      if (element.specialIncome > 0) {
        let index = vertical.indexOf(element.specialIncomeFrom);
        if (index === -1) {
          index = vertical.length;
          vertical.push(element.specialIncomeFrom);
        }
        if (!showData[index]) {
          showData[index] = {};
        }
        if (showData[index][month]) {
          showData[index][month] += element.specialIncome || 0;
        } else {
          showData[index][month] = element.specialIncome || 0;
        }
      }
    }

    for (let i = 0; i < showData.length; i++) {
      if (!showData[i]) {
        showData[i] = {};
      }
      showData[i].title = vertical[i];
      showData[i].key = vertical[i] || i;
      showData[i].total = 0;
      for (let j = 1; j <= 12; j++) {
        if (showData[i][j]) {
          showData[i].total += showData[i][j] || 0;
        }
      }
    }

    return showData;
  };

  // 圖表數據處理
  const chartData = useMemo(() => {
    // 檢查數據是否已經完整載入（需要有數據且統計已完成）
    if (!data.length || yearlyStats.totalIncome === 0) {
      return { timeSeriesData: [], categoryData: [], pieData: [], isDataReady: false };
    }
    
    // 時間序列數據（使用既有的 monthlyData）
    const timeSeriesData = yearlyStats.monthlyData.map(monthData => ({
      period: monthData.monthName,
      income: monthData.income,
      expenses: monthData.expenses,
      net: monthData.balance
    }));
    
    // 廠商支出數據（取前8名）
    const vendorMap = new Map();
    data.forEach(record => {
      if (record.expenses > 0 && record.vendor) {
        vendorMap.set(record.vendor, (vendorMap.get(record.vendor) || 0) + record.expenses);
      }
    });
    
    const categoryData = Array.from(vendorMap.entries())
      .sort(([,a], [,b]) => b - a)
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
    
    // 圓餅圖數據
    const pieData = [
      { name: '總收入', value: yearlyStats.totalIncome, color: '#51cf66' },
      { name: '總支出', value: yearlyStats.totalExpenses, color: '#fa5252' }
    ].filter(item => item.value > 0);
    
    return { timeSeriesData, categoryData, pieData, isDataReady: true };
  }, [data, yearlyStats]);
  

  const expenseTableData = generateExpenseTableData();
  const specialIncomeTableData = generateSpecialIncomeTableData();

  return (
    <Container size="xl" p={0}>
      <Stack gap={{ base: 'md', sm: 'xl' }}>
        {/* 標題與年份選擇 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="blue.0">
          <Stack align="center" gap="md">
            <Title order={1} size="xl" c="blue.7">
              年度總覽
            </Title>
            <YearPickerInput
              label="選擇年份"
              placeholder="選擇年份"
              value={showDate.toDate()}
              onChange={(date) => {
                if (date) {
                  setShowDate(dayjs(date));
                }
              }}
              size="lg"
              styles={{
                label: { fontSize: rem(18), fontWeight: 600 },
                input: { fontSize: rem(16), height: rem(50) }
              }}
            />
          </Stack>
        </Paper>

        {/* 年度統計卡片 */}
        <SimpleGrid cols={{ base: 1, xs: 2, sm: 2, lg: 4 }} spacing={{ base: 'sm', sm: 'lg' }}>
          <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md">
            <Group justify="apart">
              <div>
                <Text c="dimmed" size="sm" transform="uppercase" fw={700}>
                  總收入
                </Text>
                <Text fw={700} size="xl" c="green.6">
                  ${yearlyStats.totalIncome.toLocaleString()}
                </Text>
              </div>
              <ThemeIcon
                color="green"
                size={50}
                radius="md"
                variant="light"
              >
                <IconTrendingUp size="2rem" />
              </ThemeIcon>
            </Group>
          </Card>

          <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md">
            <Group justify="apart">
              <div>
                <Text c="dimmed" size="sm" transform="uppercase" fw={700}>
                  總支出
                </Text>
                <Text fw={700} size="xl" c="red.6">
                  ${yearlyStats.totalExpenses.toLocaleString()}
                </Text>
              </div>
              <ThemeIcon
                color="red"
                size={50}
                radius="md"
                variant="light"
              >
                <IconTrendingDown size="2rem" />
              </ThemeIcon>
            </Group>
          </Card>

          <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md">
            <Group justify="apart">
              <div>
                <Text c="dimmed" size="sm" transform="uppercase" fw={700}>
                  淨結餘
                </Text>
                <Text 
                  fw={700} 
                  size="xl" 
                  c={yearlyStats.balance >= 0 ? "blue.6" : "red.6"}
                >
                  ${yearlyStats.balance.toLocaleString()}
                </Text>
              </div>
              <ThemeIcon
                color={yearlyStats.balance >= 0 ? "blue" : "red"}
                size={50}
                radius="md"
                variant="light"
              >
                <IconWallet size="2rem" />
              </ThemeIcon>
            </Group>
          </Card>

          <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md">
            <Group justify="apart">
              <div>
                <Text c="dimmed" size="sm" transform="uppercase" fw={700}>
                  儲蓄率
                </Text>
                <Text fw={700} size="xl" c="blue.6">
                  {savingsRate}%
                </Text>
              </div>
              <RingProgress
                size={50}
                thickness={6}
                sections={[
                  { value: Math.max(0, Math.min(100, savingsRate)), color: savingsRate >= 0 ? 'blue' : 'red' }
                ]}
              />
            </Group>
          </Card>
        </SimpleGrid>

        {/* 洞察卡片 */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={40} radius="md" color="green" variant="light">
                  <IconChartBar size="1.5rem" />
                </ThemeIcon>
                <Title order={3} size="lg" ta="center">
                  最佳表現月份
                </Title>
                <Text size="xl" fw={700} c="green.6">
                  {bestMonth.monthName}
                </Text>
                <Text size="lg" c="green.6">
                  淨結餘: ${bestMonth.balance.toLocaleString()}
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  收入 ${bestMonth.income.toLocaleString()} - 支出 ${bestMonth.expenses.toLocaleString()}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card shadow="md" padding={{ base: 'md', sm: 'xl' }} radius="md" h="100%">
              <Stack align="center" gap="md">
                <ThemeIcon size={40} radius="md" color="orange" variant="light">
                  <IconShoppingCart size="1.5rem" />
                </ThemeIcon>
                <Title order={3} size="lg" ta="center">
                  支出最高月份
                </Title>
                <Text size="xl" fw={700} c="orange.6">
                  {highestExpenseMonth.monthName}
                </Text>
                <Text size="lg" c="orange.6">
                  支出: ${highestExpenseMonth.expenses.toLocaleString()}
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  收入 ${highestExpenseMonth.income.toLocaleString()} - 淨結餘 ${highestExpenseMonth.balance.toLocaleString()}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* 圖表區域 - 老人友善設計 */}
        <>
          <Title 
            order={2} 
            size={{ base: 'xl', sm: '2xl' }} 
            mb={{ base: 'lg', sm: 'xl' }} 
            c="blue.7" 
            ta="center"
            fw={700}
          >
            📊 視覺化分析
          </Title>
            
            {!chartData.isDataReady && (
              <Paper p="lg" bg="blue.0" mb="md">
                <Text ta="center" c="blue.8" size="lg">
                  🔄 正在載入圖表數據...
                </Text>
              </Paper>
            )}
            
            {/* Mobile 垂直佈局，Desktop 水平佈局 */}
            {chartData.isDataReady && (
              <>
                <Grid gutter={{ base: 'md', sm: 'lg', lg: 'xl' }}>
              {/* 收支趨勢圖 */}
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Paper 
                  shadow="md" 
                  radius="lg" 
                  p={{ base: 'md', sm: 'lg', lg: 'xl' }}
                  style={{ minHeight: '400px' }}
                >
                  <Title 
                    order={3} 
                    size={{ base: 'lg', sm: 'xl' }} 
                    mb={{ base: 'md', sm: 'lg' }} 
                    c="blue.7"
                    fw={600}
                    ta={{ base: 'center', sm: 'left' }}
                  >
                    📈 年度收支趨勢
                  </Title>
                  
                  
                  <ResponsiveContainer width="100%" height={380}>
                    <LineChart 
                      data={chartData.timeSeriesData}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#51cf66" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#51cf66" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#fa5252" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#fa5252" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeWidth={1.5} />
                      <XAxis 
                        dataKey="period" 
                        stroke="#333"
                      />
                      <YAxis 
                        stroke="#333"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '2px solid #339af0',
                          borderRadius: '12px',
                          fontSize: '18px',
                          fontWeight: '600',
                          padding: '15px',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                        }}
                        formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                        labelFormatter={(label) => `📅 時間: ${label}`}
                        labelStyle={{ fontSize: '20px', fontWeight: '700', color: '#1976d2' }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="income"
                        stroke="#51cf66"
                        fill="url(#incomeGradient)"
                        strokeWidth={4}
                        name="總收入"
                      />
                      <Area
                        type="monotone"
                        dataKey="expenses"
                        stroke="#fa5252"
                        fill="url(#expenseGradient)"
                        strokeWidth={4}
                        name="支出"
                      />
                      <Line
                        type="monotone"
                        dataKey="net"
                        stroke="#339af0"
                        strokeWidth={5}
                        name="淨額"
                        dot={{ fill: '#339af0', strokeWidth: 3, r: 7 }}
                        activeDot={{ r: 10, strokeWidth: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid.Col>

              {/* 收支比例圓餅圖 */}
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Paper 
                  shadow="md" 
                  radius="lg" 
                  p={{ base: 'md', sm: 'lg', lg: 'xl' }}
                  style={{ minHeight: '400px' }}
                >
                  <Title 
                    order={3} 
                    size={{ base: 'lg', sm: 'xl' }} 
                    mb={{ base: 'md', sm: 'lg' }} 
                    c="blue.7"
                    fw={600}
                    ta={{ base: 'center', sm: 'left' }}
                  >
                    🥧 收支比例分析
                  </Title>
                  <ResponsiveContainer width="100%" height={380}>
                    <PieChart>
                      <Pie
                        data={chartData.pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid.Col>
            </Grid>
            
            {/* 支出分類長條圖 - 全寬度 */}
            {chartData.categoryData.length > 0 && (
              <Paper 
                shadow="md" 
                radius="lg" 
                p={{ base: 'md', sm: 'lg', lg: 'xl' }}
                mt={{ base: 'md', sm: 'lg' }}
              >
                <Title 
                  order={3} 
                  size={{ base: 'lg', sm: 'xl' }} 
                  mb={{ base: 'md', sm: 'lg' }} 
                  c="blue.7"
                  fw={600}
                  ta="center"
                >
                  📊 主要支出類別分析（前8名）
                </Title>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData.categoryData}>
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      fontSize={12}
                    />
                    <YAxis 
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '2px solid #fa5252',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        padding: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [`$${value.toLocaleString()}`, '💸 支出金額']}
                      labelFormatter={(label) => `🏪 廠商: ${label}`}
                      labelStyle={{ fontSize: '16px', fontWeight: '700', color: '#d32f2f' }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#fa5252" 
                      radius={[4, 4, 0, 0]}
                      stroke="#ffffff"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}
              </>
            )}
        </>

        {/* 每月詳細資料 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
          <Title order={2} size="lg" mb="md" c="blue.7">
            每月收支明細
          </Title>
          <Grid>
            {yearlyStats.monthlyData.map((monthData) => (
              <Grid.Col key={monthData.month} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
                <Card 
                  shadow="sm" 
                  padding={{ base: 'sm', sm: 'lg' }} 
                  radius="md" 
                  withBorder
                  style={{
                    borderLeft: monthData.balance >= 0 
                      ? `${rem(4)} solid #51cf66` 
                      : `${rem(4)} solid #fa5252`
                  }}
                >
                  <Stack gap="sm">
                    <Group justify="space-between" align="center">
                      <Text size="lg" fw={600} c="dark.7">
                        {monthData.monthName}
                      </Text>
                      <Badge 
                        color={monthData.balance >= 0 ? "green" : "red"}
                        variant="light"
                        size="sm"
                      >
                        {monthData.balance >= 0 ? "盈餘" : "虧損"}
                      </Badge>
                    </Group>

                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconCoin size={16} color="#40c057" />
                        <Text size="sm" c="green.6">
                          收入: ${monthData.generalIncome.toLocaleString()}
                        </Text>
                      </Group>

                      <Group gap="xs">
                        <IconGift size={16} color="#ffa94d" />
                        <Text size="sm" c="orange.6">
                          特別收入: ${monthData.specialIncome.toLocaleString()}
                        </Text>
                      </Group>

                      <Group gap="xs">
                        <IconSum size={16} color="#9775fa" />
                        <Text size="sm" c="grape.6">
                          總收入: ${monthData.income.toLocaleString()}
                        </Text>
                      </Group>
                      
                      <Group gap="xs">
                        <IconShoppingCart size={16} color="#fa5252" />
                        <Text size="sm" c="red.6">
                          支出: ${monthData.expenses.toLocaleString()}
                        </Text>
                      </Group>
                      
                      <Group gap="xs">
                        <IconWallet size={16} color={monthData.balance >= 0 ? "#339af0" : "#fa5252"} />
                        <Text 
                          size="sm" 
                          fw={500}
                          c={monthData.balance >= 0 ? "blue.6" : "red.6"}
                        >
                          結餘: ${monthData.balance.toLocaleString()}
                        </Text>
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>

        {/* 支出明細表格 */}
        {expenseTableData.showData.length > 0 && (
          <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
            <Title order={2} size="lg" mb="md" c="blue.7">
              收支明細表
            </Title>
            <ScrollArea>
              <Table fontSize="sm" withRowBorders withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>月份</Table.Th>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Table.Th key={i + 1} style={{ textAlign: 'center', minWidth: rem(70) }}>
                        {String(i + 1).padStart(2, '0')}
                      </Table.Th>
                    ))}
                    <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>總計</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {expenseTableData.showData.map((row, index) => {
                    const isIncomeRow = index === 0;
                    return row.title && (
                      <Table.Tr 
                        key={row.key || index}
                        style={{
                          backgroundColor: isIncomeRow ? '#f0f9ff' : index % 2 === 1 ? '#fafafa' : 'white'
                        }}
                      >
                        <Table.Td style={{ 
                          textAlign: 'center', 
                          fontWeight: 600,
                          color: isIncomeRow ? '#0369a1' : '#374151'
                        }}>
                          {isIncomeRow ? '<收入>' : row.title}
                        </Table.Td>
                        {Array.from({ length: 12 }, (_, i) => (
                          <Table.Td key={i + 1} style={{ 
                            textAlign: 'right',
                            color: isIncomeRow ? '#059669' : '#dc2626',
                            fontWeight: isIncomeRow ? 600 : 'normal'
                          }}>
                            {row[i + 1] ? format_with_substring(row[i + 1]) : ''}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ 
                          textAlign: 'right', 
                          fontWeight: 600,
                          color: isIncomeRow ? '#059669' : '#dc2626'
                        }}>
                          {row.total ? format_with_substring(row.total) : ''}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {/* 總計欄 */}
                  <Table.Tr style={{ backgroundColor: '#f8f9fa' }}>
                    <Table.Td style={{ textAlign: 'center', fontWeight: 700 }}>
                      總計
                    </Table.Td>
                    {Array.from({ length: 12 }, (_, i) => {
                      let monthTotal = 0;
                      expenseTableData.showData.forEach((row, index) => {
                        if (row[i + 1]) {
                          if (index === 0) {
                            monthTotal += row[i + 1] || 0;
                          } else {
                            monthTotal -= row[i + 1] || 0;
                          }
                        }
                      });
                      return (
                        <Table.Td key={i + 1} style={{ textAlign: 'right', fontWeight: 600 }}>
                          {monthTotal !== 0 ? format_with_substring(monthTotal) : ''}
                        </Table.Td>
                      );
                    })}
                    <Table.Td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {format_with_substring(yearlyStats.balance)}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}

        {/* 特殊收入表格 */}
        {specialIncomeTableData.length > 0 && (
          <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
            <Title order={2} size="lg" mb="md" c="blue.7">
              特殊收入明細
            </Title>
            <ScrollArea>
              <Table fontSize="sm" withRowBorders withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>月份</Table.Th>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Table.Th key={i + 1} style={{ textAlign: 'center', minWidth: rem(70) }}>
                        {String(i + 1).padStart(2, '0')}
                      </Table.Th>
                    ))}
                    <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>總計</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {specialIncomeTableData.map((row, index) => (
                    row.title && (
                      <Table.Tr key={row.key || index}>
                        <Table.Td style={{ textAlign: 'center', fontWeight: 600 }}>
                          {row.title}
                        </Table.Td>
                        {Array.from({ length: 12 }, (_, i) => (
                          <Table.Td key={i + 1} style={{ textAlign: 'right' }}>
                            {row[i + 1] ? format_with_substring(row[i + 1]) : ''}
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {row.total ? format_with_substring(row.total) : ''}
                        </Table.Td>
                      </Table.Tr>
                    )
                  ))}
                  {/* 總計欄 */}
                  <Table.Tr style={{ backgroundColor: '#f8f9fa' }}>
                    <Table.Td style={{ textAlign: 'center', fontWeight: 700 }}>
                      總計
                    </Table.Td>
                    {Array.from({ length: 12 }, (_, i) => {
                      let monthTotal = 0;
                      specialIncomeTableData.forEach((row) => {
                        if (row[i + 1]) {
                          monthTotal += row[i + 1] || 0;
                        }
                      });
                      return (
                        <Table.Td key={i + 1} style={{ textAlign: 'right', fontWeight: 600 }}>
                          {monthTotal !== 0 ? format_with_substring(monthTotal) : ''}
                        </Table.Td>
                      );
                    })}
                    <Table.Td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {format_with_substring(
                        specialIncomeTableData.reduce((acc, row) => acc + (row.total || 0), 0)
                      )}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};

export default ElderlyBoardIndex;
